'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { WizardStepIndicator } from './wizard-step-indicator';
import { StepBasicInfo } from './step-basic-info';
import { StepAddFields } from './step-add-fields';
import { StepPreviewSave } from './step-preview-save';
import { dynamicFormQueries } from '../services/dynamic-forms.queries';
import {
  useCreateDynamicFormMutation,
  useUpdateDynamicFormMutation,
  useSyncFieldsMutation,
  useActivateDynamicFormMutation
} from '../services/dynamic-forms.mutations';
import type { DynamicForm, LocalField } from '../types';
import type { BasicInfoFormValues } from '../schemas';

interface WizardContentProps {
  editingForm?: DynamicForm | null;
  previewOnly: boolean;
  onClose: () => void;
}

function formToLocalFields(form: DynamicForm): LocalField[] {
  if (!form.fields) return [];
  return form.fields.map((f) => ({
    id: f.id,
    tempId: crypto.randomUUID(),
    label: f.label,
    type: f.type,
    is_required: f.is_required,
    options: f.options ?? [],
    is_multi_select: f.is_multi_select ?? false,
    default_value: f.default_value ?? '',
    sort_order: f.sort_order
  }));
}

export function WizardContent({
  editingForm,
  previewOnly,
  onClose
}: WizardContentProps) {
  const t = useTranslations('dynamicForms');
  const tv = useTranslations('validation');

  const [currentStep, setCurrentStep] = useState(previewOnly ? 2 : 0);
  const [draftFormId, setDraftFormId] = useState<number | null>(
    editingForm?.id ?? null
  );
  const [basicInfo, setBasicInfo] = useState<BasicInfoFormValues | null>(
    editingForm
      ? {
          name: editingForm.name,
          type: editingForm.type,
          is_default: editingForm.is_default,
          is_double_sided: editingForm.is_double_sided ?? false,
          offices: editingForm.offices ?? []
        }
      : null
  );
  const [containerTypeId, setContainerTypeId] = useState<number | ''>(
    editingForm?.container_type_id ?? ''
  );
  const [boxTypeId, setBoxTypeId] = useState<number | ''>(
    editingForm?.box_type_id ?? ''
  );
  const [localFields, setLocalFields] = useState<LocalField[]>(
    editingForm ? formToLocalFields(editingForm) : []
  );
  const [hasLoadedDetail, setHasLoadedDetail] = useState(false);

  const createMutation = useCreateDynamicFormMutation();
  const updateMutation = useUpdateDynamicFormMutation();
  const syncFieldsMutation = useSyncFieldsMutation();
  const activateMutation = useActivateDynamicFormMutation();

  const { data: fullFormResponse } = useQuery({
    ...dynamicFormQueries.detail(editingForm?.id ?? 0),
    enabled: !!editingForm?.id
  });

  // When the detail query resolves, populate fields (once)
  if (fullFormResponse?.data && !hasLoadedDetail) {
    setHasLoadedDetail(true);
    const form = fullFormResponse.data;
    setBasicInfo({
      name: form.name,
      type: form.type,
      is_default: form.is_default,
      is_double_sided: form.is_double_sided ?? false,
      offices: form.offices ?? []
    });
    setContainerTypeId(form.container_type_id ?? '');
    setBoxTypeId(form.box_type_id ?? '');
    setLocalFields(formToLocalFields(form));
  }

  const isSaving = syncFieldsMutation.isPending || activateMutation.isPending;

  function handleBasicInfoNext(values: BasicInfoFormValues) {
    setBasicInfo(values);
    setCurrentStep(1);
  }

  async function handleFieldsNext() {
    if (localFields.length === 0) {
      toast.error(t('fieldsRequired'));
      return;
    }
    const incomplete = localFields.some((f) => !f.label.trim());
    if (incomplete) {
      toast.error(t('fieldsRequired'));
      return;
    }
    if (!basicInfo) return;

    // Which type the form pins follows its own type: a packing form feeds the box
    // form (box type), a registration form feeds the container form.
    const isPackingForm = basicInfo.type === 'filling';
    if (isPackingForm ? !boxTypeId : !containerTypeId) {
      toast.error(
        tv(
          isPackingForm
            ? 'dynamicFormBoxTypeRequired'
            : 'dynamicFormContainerTypeRequired'
        )
      );
      return;
    }

    const values = isPackingForm
      ? { ...basicInfo, box_type_id: boxTypeId as number }
      : { ...basicInfo, container_type_id: containerTypeId as number };

    try {
      if (draftFormId) {
        await updateMutation.mutateAsync({
          id: draftFormId,
          data: values
        });
      } else {
        const result = await createMutation.mutateAsync(values);
        const created = result?.data;
        if (created?.id) {
          setDraftFormId(created.id);
        }
      }
      setCurrentStep(2);
    } catch {
      // mutation already handles error toast
    }
  }

  async function handleSave() {
    if (!draftFormId) return;

    try {
      await syncFieldsMutation.mutateAsync({
        id: draftFormId,
        data: {
          fields: localFields.map((f, i) => ({
            ...(f.id ? { id: f.id } : {}),
            label: f.label,
            type: f.type,
            is_required: f.is_required,
            options:
              f.type === 'selection_list'
                ? f.options.filter((o) => o.trim())
                : null,
            is_multi_select:
              f.type === 'selection_list' ? f.is_multi_select : undefined,
            default_value:
              f.type === 'selection_list' && f.default_value
                ? f.default_value
                : f.type === 'number' && f.default_value
                  ? f.default_value
                  : null,
            sort_order: i
          }))
        }
      });

      await activateMutation.mutateAsync(draftFormId);
      toast.success(t('saveSuccess'));
      onClose();
    } catch {
      // mutation already handles error toast
    }
  }

  return (
    <>
      <WizardStepIndicator currentStep={currentStep} />

      {currentStep === 0 && !previewOnly && (
        <StepBasicInfo
          defaultValues={basicInfo ?? undefined}
          onNext={handleBasicInfoNext}
        />
      )}

      {currentStep === 1 && !previewOnly && (
        <StepAddFields
          fields={localFields}
          onFieldsChange={setLocalFields}
          formType={basicInfo?.type ?? editingForm?.type}
          containerTypeId={containerTypeId}
          onContainerTypeIdChange={setContainerTypeId}
          boxTypeId={boxTypeId}
          onBoxTypeIdChange={setBoxTypeId}
          onNext={handleFieldsNext}
          onPrevious={() => setCurrentStep(0)}
          onCancel={onClose}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {currentStep === 2 && (
        <StepPreviewSave
          formName={basicInfo?.name ?? editingForm?.name ?? ''}
          fields={localFields}
          onSave={handleSave}
          onPrevious={() => setCurrentStep(1)}
          isSaving={isSaving}
          readOnly={previewOnly}
          onCancel={previewOnly ? undefined : onClose}
        />
      )}
    </>
  );
}
